import React, { useState, useEffect, useContext } from "react";
import {
  Collapse,
  Form,
  Modal,
  Button,
  Spinner,
  Offcanvas,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { clearAllTenants, getTenants } from "../../../redux/slices/tenants";
import NotyfContext from "../../../contexts/NotyfContext";
import { updateUserRole } from "../../../redux/slices/user";
import { Formik } from "formik";
import { clearAllManagers, getManagers } from "../../../redux/slices/managers";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../../hooks/useWindowSize";
import {
  clearAllCustomers,
  getCustomers,
} from "../../../redux/slices/customers";
import useDeviceType from "../../../hooks/useDeviceType";

const UpdatePrivilegeModal = ({
  showPrivilegeModal,
  setShowPrivilegeModal,
  entityUser,
}) => {
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const { tenants } = useSelector((state) => state.tenants);
  const { managers } = useSelector((state) => state.managers);

  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const { t } = useTranslation();

  const [activeUser, setActiveUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(showPrivilegeModal);
  const [fadeOut, setFadeOut] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);

  // open/close animation for mobile
  useEffect(() => {
    if (showPrivilegeModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [showPrivilegeModal, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setShowPrivilegeModal(false);
    }, 300);
  };

  // choose active user (impersonation aware)
  useEffect(() => {
    const au =
      impersonationUser && impersonationUser.id ? impersonationUser : user;
    setActiveUser(au || {});
  }, [user?.id, impersonationUser?.id]); // depend on ids to avoid object churn

  // fetch lists according to active user's role
  useEffect(() => {
    const load = async () => {
      dispatch(clearAllTenants());
      dispatch(clearAllManagers());
      dispatch(clearAllCustomers());

      // Only fetch relevant lists
      if (!activeUser?.id || !showPrivilegeModal) return;

      const tasks = [];
      if (
        activeUser.role === "admin" ||
        (activeUser.role === "subaccount" && activeUser.admin != null)
      ) {
        tasks.push(dispatch(getTenants()));
      }
      if (
        activeUser.role === "admin" ||
        activeUser.role === "tenant" ||
        (activeUser.role === "subaccount" &&
          (activeUser.admin != null || activeUser.tenant != null))
      ) {
        tasks.push(dispatch(getManagers()));
      }
      if (
        activeUser.role === "admin" ||
        activeUser.role === "tenant" ||
        activeUser.role === "manager" ||
        (activeUser.role === "subaccount" &&
          (activeUser.admin != null ||
            activeUser.tenant != null ||
            activeUser.manager != null))
      ) {
        tasks.push(dispatch(getCustomers()));
      }

      await Promise.all(tasks);
      setIsLoading(false);
    };

    if (showPrivilegeModal) {
      setIsLoading(true);
      load();
    }
  }, [activeUser?.id, activeUser?.role, showPrivilegeModal, dispatch]);

  // available roles given who is acting and target's current privilege
  const getAvailableRoles = ({ activeUser, entityUser }) => {
    if (activeUser.role === "admin") {
      if (entityUser.privilege === "TENANT") {
        return [
          { value: "manager", label: "Manager" },
          { value: "customer", label: "Customer" },
        ];
      } else if (entityUser.privilege === "MANAGER") {
        return [
          { value: "tenant", label: "Tenant" },
          { value: "customer", label: "Customer" },
        ];
      } else if (entityUser.privilege === "CUSTOMER") {
        return [
          { value: "tenant", label: "Tenant" },
          { value: "manager", label: "Manager" },
        ];
      }
    } else if (activeUser.role === "tenant") {
      if (entityUser.privilege === "MANAGER") {
        return [{ value: "customer", label: "Customer" }];
      } else if (entityUser.privilege === "CUSTOMER") {
        return [{ value: "manager", label: "Manager" }];
      }
    }
    return [];
  };

  useEffect(() => {
    if (activeUser?.id) {
      setRoleOptions(getAvailableRoles({ activeUser, entityUser }));
    }
  }, [activeUser?.id, activeUser?.role, entityUser?.privilege]);

  const initialValues = {
    newRole: "",
    parentId: "", // set by user for cases that need it; auto-set for tenant->manager
  };

  const validation = Yup.object().shape({
    newRole: Yup.string().required(t("common.required") || "Required"),
    parentId: Yup.number()
      .nullable()
      .when("newRole", {
        // admin: manager/customer require parentId
        // tenant:
        //  - manager: parentId auto-set, hidden (still valid)
        //  - customer: requires parentId (tenant or one of tenant's managers)
        is: (val) => val === "manager" || val === "customer",
        then: (schema) =>
          schema.required(
            t("crm.roleModal.parentRequired") || "Parent entity is required"
          ),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  const SubmitButtons = ({ onCancel }) => (
    <Modal.Footer>
      <Button
        variant="primary"
        style={{
          "--dynamic-bg-color": whitelabel?.backgroundColor ?? "#0E1A2B",
          "--dynamic-font-color": whitelabel?.fontColor ?? "#fff",
          border: "none",
        }}
        onClick={onCancel}
      >
        {t("crm.roleModal.cancel")}
      </Button>
      <Button
        type="submit"
        variant="primary"
        style={{
          "--dynamic-bg-color": whitelabel?.backgroundColor ?? "#0E1A2B",
          "--dynamic-font-color": whitelabel?.fontColor ?? "#fff",
          border: "none",
        }}
      >
        {t("crm.roleModal.submit")}
      </Button>
    </Modal.Footer>
  );

  const RoleSelect = ({ values, handleChange, setFieldValue }) => (
    <Form.Group className="mb-3">
      <Form.Label>{t("crm.roleModal.role")}</Form.Label>
      <Form.Select
        name="newRole"
        value={values.newRole}
        onChange={(e) => {
          const nextRole = e.target.value;
          handleChange(e);

          // IMPORTANT: only set parentId here (event handler), not during render
          if (activeUser.role === "tenant") {
            if (nextRole === "manager") {
              // auto-assign parentId to active tenant
              setFieldValue("parentId", activeUser.id);
            } else if (nextRole === "customer") {
              // force user to pick between tenant or one of its managers
              setFieldValue("parentId", "");
            } else {
              setFieldValue("parentId", "");
            }
          } else if (activeUser.role === "admin") {
            // admin must select parent for manager/customer
            if (nextRole === "manager" || nextRole === "customer") {
              setFieldValue("parentId", "");
            } else {
              setFieldValue("parentId", "");
            }
          } else {
            // default
            setFieldValue("parentId", "");
          }
        }}
      >
        <option value="">
          {t("crm.roleModal.selectRole") || "Select role"}
        </option>
        {roleOptions.map((role) => (
          <option key={role.value} value={role.value}>
            {role.label}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );

  const ParentSelectForAdmin = ({ values, errors, touched, handleChange }) => (
    <Collapse
      in={["manager", "customer"].includes(values.newRole)}
      dimension="height"
    >
      <div>
        <Form.Group className="mb-3">
          <Form.Label>{t("crm.roleModal.parentEntity")}</Form.Label>
          <Form.Select
            name="parentId"
            value={values.parentId}
            isInvalid={Boolean(touched.parentId && errors.parentId)}
            onChange={handleChange}
          >
            <option value="">{t("crm.roleModal.selectParent")}</option>

            {values.newRole === "manager" && (
              <optgroup label="Tenants">
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {`${tenant.fullName} (${tenant.company})`}
                  </option>
                ))}
              </optgroup>
            )}

            {values.newRole === "customer" && (
              <>
                <optgroup label="Tenants">
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {`${tenant.fullName} (${tenant.company})`}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Managers">
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {`${manager.fullName} (${manager.company})`}
                    </option>
                  ))}
                </optgroup>
              </>
            )}
          </Form.Select>
        </Form.Group>
      </div>
    </Collapse>
  );

  const ParentSelectForTenant = ({ values, errors, touched, handleChange }) => {
    // Only visible for tenant → customer
    const show = values.newRole === "customer";
    return (
      <Collapse in={show} dimension="height">
        <div>
          <Form.Group className="mb-3">
            <Form.Label>{t("crm.roleModal.parentEntity")}</Form.Label>
            <Form.Select
              name="parentId"
              value={values.parentId}
              isInvalid={Boolean(touched.parentId && errors.parentId)}
              onChange={handleChange}
            >
              <option value="">{t("crm.roleModal.selectParent")}</option>
              <optgroup label="Tenants">
                <option value={activeUser.id}>
                  {(activeUser.fullName || activeUser.company) ??
                    `#${activeUser.id}`}{" "}
                  (TENANT)
                </option>
              </optgroup>
              <optgroup label="Managers">
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {(m.fullName || m.company) ?? `#${m.id}`} (MANAGER)
                  </option>
                ))}
              </optgroup>
            </Form.Select>
          </Form.Group>
        </div>
      </Collapse>
    );
  };

  const onSubmit = async (values) => {
    // if same role, bail
    if (values.newRole === entityUser.privilege?.toLowerCase()) {
      setShowPrivilegeModal(false);
      return;
    }

    const data = {
      role: values.newRole,
      parentId:
        values.newRole === "manager"
          ? // tenant performing: parentId auto-assigned previously
            values.parentId ||
            (activeUser.role === "tenant" ? activeUser.id : null)
          : values.newRole === "customer"
          ? values.parentId || null
          : null,
    };

    try {
      await dispatch(updateUserRole({ entityUser, data })).unwrap?.();
      notyf.open({
        type: "success",
        message: t("crm.roleModal.success") || "Role updated successfully",
        duration: 5000,
        dismissible: false,
        ripple: true,
        position: { x: "center", y: "top" },
      });
    } catch (error) {
      notyf.open({
        type: "error",
        message: error?.message || String(error),
        duration: 5000,
        dismissible: false,
        ripple: true,
        position: { x: "center", y: "top" },
      });
    } finally {
      setShowPrivilegeModal(false);
    }
  };

  // DESKTOP
  if (isDesktop) {
    return (
      <Modal
        show={showPrivilegeModal}
        onHide={() => setShowPrivilegeModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("crm.roleModal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={onSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => {
              if (isLoading) {
                return (
                  <div className="text-center">
                    <Spinner
                      animation="border"
                      variant="primary"
                      className="me-2"
                    />
                  </div>
                );
              }
              return (
                <Form onSubmit={handleSubmit}>
                  <RoleSelect
                    values={values}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                  />

                  {activeUser.role === "admin" && (
                    <ParentSelectForAdmin
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                    />
                  )}

                  {activeUser.role === "tenant" && (
                    <ParentSelectForTenant
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                    />
                  )}

                  <SubmitButtons
                    onCancel={() => setShowPrivilegeModal(false)}
                  />
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  }

  // MOBILE
  return (
    <Offcanvas
      show={internalShow}
      placement="bottom"
      backdrop="static"
      scrollable
      className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
      onHide={handleHide}
    >
      <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
        <Offcanvas.Title>{t("crm.roleModal.title")}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="fullscreen-offcanvas-body">
        <Formik
          initialValues={initialValues}
          validationSchema={validation}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => {
            if (isLoading) {
              return (
                <div className="text-center">
                  <Spinner
                    animation="border"
                    variant="primary"
                    className="me-2"
                  />
                </div>
              );
            }
            return (
              <Form onSubmit={handleSubmit}>
                <RoleSelect
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />

                {activeUser.role === "admin" && (
                  <ParentSelectForAdmin
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                  />
                )}

                {activeUser.role === "tenant" && (
                  <ParentSelectForTenant
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                  />
                )}

                <div className="d-flex flex-column justify-content-center mt-3">
                  <Button
                    type="submit"
                    variant="primary"
                    style={{
                      "--dynamic-bg-color":
                        whitelabel?.backgroundColor ?? "#0E1A2B",
                      "--dynamic-font-color": whitelabel?.fontColor ?? "#fff",
                      border: "none",
                      padding: "10px",
                    }}
                    className="mb-3"
                  >
                    {t("crm.roleModal.submit")}
                  </Button>
                  <Button
                    variant="primary"
                    style={{
                      "--dynamic-bg-color":
                        whitelabel?.backgroundColor ?? "#0E1A2B",
                      "--dynamic-font-color": whitelabel?.fontColor ?? "#fff",
                      border: "none",
                      padding: "10px",
                    }}
                    onClick={handleHide}
                  >
                    {t("crm.roleModal.cancel")}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default UpdatePrivilegeModal;
